import { ODOO_BASE_URL } from '../../odooConfig';

const isLocalDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const BASE_URL = isLocalDevelopment ? '' : ODOO_BASE_URL;
// Same mount as HR login so the webpack proxy already forwards this path.
// `/api/exams` 404s on localhost:3000 unless npm start is restarted after setupProxy changes.
const API_PREFIX = '/api/employee-attendance/hr/exams';
const MARKS_PREFIX = '/api/marks';

class ExamAPI {
  constructor(baseURL = BASE_URL) {
    this.baseURL = baseURL;
    this.onUnauthorized = null;
  }

  setUnauthorizedHandler(handler) {
    this.onUnauthorized = handler;
  }

  async request(endpoint, params = {}, token = null, options = {}) {
    const { suppressUnauthorized = false } = options;
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'call',
        params,
        id: Date.now(),
      }),
    });

    if (!suppressUnauthorized && response.status === 401 && this.onUnauthorized) {
      this.onUnauthorized();
    }

    if (!response.ok) {
      const errorText = await response.text();
      const err = new Error(
        response.status === 404
          ? `Endpoint not found (404): ${endpoint}`
          : errorText || response.statusText
      );
      err.httpStatus = response.status;
      err.code = response.status;
      throw err;
    }

    const data = await response.json();
    if (data.error) {
      const nested =
        data.error.data?.message ||
        data.error.data?.debug ||
        data.error.message ||
        'API request failed';
      const message =
        String(nested)
          .replace(/^Odoo Server Error:?\s*/i, '')
          .split('\n')[0]
          .trim() || 'API request failed';
      const err = new Error(message);
      err.errorData = data.error;
      err.code = data.error.code;
      throw err;
    }

    const result = data.result ?? data;
    if (result?.success === false) {
      const code = result.code ?? 400;
      if (!suppressUnauthorized && (code === 401 || result.error === 'Unauthorized')) {
        if (this.onUnauthorized) this.onUnauthorized();
      }
      const message =
        result.message || result.error || data.error?.message || 'API request failed';
      const err = new Error(message);
      err.code = code;
      err.result = result;
      throw err;
    }
    return result;
  }

  getEntries(params, token) {
    return this.request(`${API_PREFIX}/entries`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getEntry(params, token) {
    return this.request(`${API_PREFIX}/entries/get`, params, token, {
      suppressUnauthorized: true,
    });
  }

  previewStudents(params, token) {
    return this.request(`${API_PREFIX}/entries/preview-students`, params, token, {
      suppressUnauthorized: true,
    });
  }

  createEntry(params, token) {
    return this.request(`${API_PREFIX}/entries/create`, params, token, {
      suppressUnauthorized: true,
    });
  }

  updateEntry(params, token) {
    return this.request(`${API_PREFIX}/entries/update`, params, token, {
      suppressUnauthorized: true,
    });
  }

  exportEntry(params, token) {
    return this.request(`${API_PREFIX}/entries/export`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getFaculties(token) {
    return this.request(`${API_PREFIX}/lookups/faculties`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  getDepartments(facultyId, token) {
    return this.request(
      `${API_PREFIX}/lookups/departments`,
      { faculty_id: facultyId || undefined },
      token,
      { suppressUnauthorized: true }
    );
  }

  getBatches(departmentId, token, extra = {}) {
    return this.request(
      `${API_PREFIX}/lookups/batches`,
      { department_id: departmentId || undefined, ...extra },
      token,
      { suppressUnauthorized: true }
    );
  }

  getSubjects(params, token) {
    return this.request(`${API_PREFIX}/lookups/subjects`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getSemesters(token) {
    return this.request(`${API_PREFIX}/lookups/semesters`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  getAcademicYears(token) {
    return this.request(`${API_PREFIX}/lookups/academic-years`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  // ---- Teacher Sheet Marks (/api/marks) ----

  getTeacherSheets(params, token) {
    return this.request(`${MARKS_PREFIX}/teacher-sheet/list`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getTeacherSheet(sheetId, token) {
    return this.request(`${MARKS_PREFIX}/teacher-sheet/${Number(sheetId)}`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  updateTeacherSheet(params, token) {
    return this.request(`${MARKS_PREFIX}/teacher-sheet/update`, params, token, {
      suppressUnauthorized: true,
    });
  }

  searchStudentMarks(params, token) {
    return this.request(`${MARKS_PREFIX}/student/search`, params, token, {
      suppressUnauthorized: true,
    });
  }

  lookupStudent(params, token) {
    return this.request(`${MARKS_PREFIX}/student/lookup`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getStudentTranscript(params, token) {
    return this.request(`${MARKS_PREFIX}/student/transcript`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getStudentTranscriptPdf(params, token) {
    return this.request(`${MARKS_PREFIX}/student/transcript-pdf`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getMarksStudentList(params, token) {
    return this.request(`${MARKS_PREFIX}/student-list`, params, token, {
      suppressUnauthorized: true,
    });
  }
}

const examAPI = new ExamAPI();
export default examAPI;
